import { useEffect, useMemo, useState } from 'react';
import ErrorDisplay from '../../../../components/ErrorDisplay';
import { ToolPicker } from '../../../../pickers/tool/ToolPicker';
import type { Tool } from '../../../../types/tools';
import type { QuestTemplateFirestore, QuestTemplateToolFirestore } from '../../services/questTypes';
import { buildResourceUsageMap } from '../../utils/questUsageUtils';
import { QuestFormActions } from './QuestFormActions';
import { QuestFormFields } from './QuestFormFields';
import { QuestFormSectionDivider } from './QuestFormSectionDivider';
import { useQuestForm } from './useQuestForm';

interface ToolSpecificFormData {
    imageUrl: string;
    toolName: string;
    toolId: string;
}

interface ToolQuestFormProps {
    questTemplate?: QuestTemplateToolFirestore;
    initialOrder: number;
    onSave: (questTemplate: QuestTemplateToolFirestore) => void;
    onSaveAndContinue?: (questTemplate: QuestTemplateToolFirestore) => void;
    onCancel: () => void;
    allQuestTemplates: QuestTemplateFirestore[];
}

export const ToolQuestForm = ({ questTemplate, initialOrder, onSave, onSaveAndContinue, onCancel, allQuestTemplates }: ToolQuestFormProps) => {
    const {
        commonFormData,
        loading,
        error,
        handleCommonChange,
        handleSubmit,
        handleSaveAndContinue
    } = useQuestForm({
        questTemplate,
        initialOrder,
        defaultProgressType: 'fixed'
    });

    const [specificFormData, setSpecificFormData] = useState<ToolSpecificFormData>({
        imageUrl: '',
        toolName: '',
        toolId: '',
    });

    // Build usage map for tools
    const toolUsageMap = useMemo(() => {
        return buildResourceUsageMap(allQuestTemplates, 'toolId');
    }, [allQuestTemplates]);

    useEffect(() => {
        if (questTemplate && questTemplate.id) {
            setSpecificFormData({
                imageUrl: questTemplate.imageUrl,
                toolName: questTemplate.toolName,
                toolId: questTemplate.toolId,
            });
        }
    }, [questTemplate]);

    const handleSpecificChange = (field: string, value: string | boolean) => {
        setSpecificFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleChange = (field: string, value: string | boolean) => {
        if (field in commonFormData) {
            handleCommonChange(field as keyof typeof commonFormData, value as string | boolean);
        } else {
            handleSpecificChange(field, value);
        }
    };

    const validateSpecificFields = (): string | null => {
        if (!specificFormData.imageUrl.trim()) {
            return 'Image URL is required for tool quests';
        }
        if (!specificFormData.toolName.trim()) {
            return 'Tool Name is required for tool quests';
        }
        if (!specificFormData.toolId.trim()) {
            return 'Tool ID is required for tool quests';
        }
        return null;
    };

    const createQuestTemplate = (baseData: Partial<QuestTemplateToolFirestore>): QuestTemplateToolFirestore => {
        return {
            ...baseData,
            type: 'tool',
            maxProgress: 1,
            imageUrl: specificFormData.imageUrl,
            toolName: specificFormData.toolName,
            toolId: specificFormData.toolId,
        } as QuestTemplateToolFirestore;
    };

    const onSubmit = handleSubmit(validateSpecificFields, createQuestTemplate, onSave);
    const onSaveAndContinueClick = onSaveAndContinue ? handleSaveAndContinue(validateSpecificFields, createQuestTemplate, onSaveAndContinue) : undefined;

    return (
        <form onSubmit={onSubmit} className="space-y-4">
            {error && (
                <ErrorDisplay message={error} />
            )}

            <QuestFormFields
                formData={commonFormData}
                questType="tool"
                disableProgressType={true}
                onChange={handleChange}
            />

            <QuestFormSectionDivider title="Tool-specific fields" />

            {/* Tool-specific fields */}
            <ToolPicker
                value={specificFormData.toolId}
                onChange={(tool: Tool | null) => {
                    if (tool) {
                        setSpecificFormData(prev => ({
                            ...prev,
                            toolId: tool.id,
                            toolName: tool.bannerText,
                            imageUrl: tool.bannerPath
                        }));
                    } else {
                        setSpecificFormData(prev => ({
                            ...prev,
                            toolId: '',
                            toolName: '',
                            imageUrl: ''
                        }));
                    }
                }}
                label="Tool"
                required
                usageMap={toolUsageMap}
            />

            {error && (
                <ErrorDisplay message={error} />
            )}

            <QuestFormActions
                onCancel={onCancel}
                onSaveAndContinue={onSaveAndContinueClick}
                loading={loading}
                isEditMode={!!questTemplate?.id}
            />
        </form>
    );
};
